import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          params.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9nisr8bdkpk99j7gk8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || '';

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };
  const milestones = [
    { year: '2018', title: '平台创立', description: '成全影视正式上线，开始为用户提供免费高清影视内容。' },
    { year: '2019', title: '用户突破百万', description: '凭借优质的内容和流畅的体验，注册用户突破100万。' },
    { year: '2020', title: '内容扩展', description: '影视资源库扩展到10万+，涵盖电影、剧集、动漫、综艺等多种类型。' },
    { year: '2022', title: '4K高清升级', description: '全面升级视频画质，支持4K超高清在线播放。' },
    { year: '2024', title: 'AI智能推荐', description: '引入AI智能推荐算法，为用户提供个性化的观影体验。' },
    { year: '2026', title: '全新品牌升级', description: '品牌全面焕新，用户体验进一步优化，内容生态更加丰富。' },
  ];

  const testimonials = [
    {
      name: '小王',
      role: '资深影迷',
      content: '用了成全影视三年多了，资源更新快，画质好，关键是完全免费。推荐给了身边所有朋友！',
    },
    {
      name: '李女士',
      role: '追剧达人',
      content: '追剧神器！热门剧集更新速度特别快，而且没有广告干扰，观剧体验一流。',
    },
    {
      name: '张同学',
      role: '大学生',
      content: '作为学生党，免费又高清的成全影视真的是宝藏。动漫资源也特别全，每周都在上面追新番。',
    },
    {
      name: '陈先生',
      role: '家庭用户',
      content: '全家都在用成全影视。周末和孩子一起看动画电影，晚上和爱人追剧，已经成为我们家的日常。',
    },
  ];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于成全影视',
    description: '成全影视是领先的高清免费在线影视平台，提供电影、电视剧、动漫、综艺等海量免费高清内容。',
    url: 'https://bjtongshiji.cn/about',
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(aboutSchema)}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="px-4 md:px-6 py-4">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { name: '首页', path: '/' },
                  { name: '关于我们' },
                ]}
              />

              <section className="mb-14">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  关于成全影视
                </h1>
                <p className="mt-3 text-sm text-foreground-500">
                  高清免费影视 · 流畅追剧体验 · 海量内容资源
                </p>

                <div className="mt-6 bg-background-100 rounded-lg p-5 md:p-8">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                    品牌介绍
                  </h2>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    成全影视成立于2018年，是国内领先的高清免费在线影视平台。我们致力于为用户提供海量优质的影视内容，涵盖电影、电视剧、动漫、综艺、纪录片等多种类型，全部内容免费在线观看，无需注册会员，无需充值VIP。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    作为一家以用户体验为核心的影视平台，成全影视始终坚持"免费、高清、流畅"的服务理念。我们拥有专业的内容运营团队，第一时间更新最新影视资源，确保用户不错过任何精彩内容。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    经过多年的发展，成全影视已积累超过<strong className="text-primary-600">10万+</strong>影视资源，覆盖各类题材和类型，日均服务数百万用户。我们采用先进的视频编码技术和全球CDN加速网络，确保用户无论身在何处都能享受流畅的观影体验。
                  </p>
                </div>
              </section>

              <section className="mb-14">
                <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                  发展历程
                </h2>
                <div className="relative">
                  <div className="absolute left-4 md:left-6 top-0 bottom-0 w-px bg-background-300"></div>
                  <div className="space-y-6">
                    {milestones.map((item, index) => (
                      <div key={index} className="relative flex items-start gap-4 md:gap-6 pl-10 md:pl-14">
                        <div className="absolute left-2 md:left-4 w-4 h-4 md:w-5 md:h-5 rounded-full bg-primary-500 border-2 border-background-50 flex-shrink-0"></div>
                        <div className="bg-white rounded-lg p-4 flex-1">
                          <span className="text-xs font-bold text-primary-600 bg-primary-100 rounded px-2 py-0.5 whitespace-nowrap">
                            {item.year}
                          </span>
                          <h3 className="font-heading text-base font-bold text-foreground-900 mt-2">
                            {item.title}
                          </h3>
                          <p className="text-sm text-foreground-600 mt-1">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="mb-14">
                <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {testimonials.map((item, index) => (
                    <div key={index} className="bg-background-100 rounded-lg p-5">
                      <div className="flex items-center gap-1 mb-3">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <i key={star} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                        "{item.content}"
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center">
                          <span className="text-sm font-bold text-secondary-700">
                            {item.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                          <p className="text-xs text-foreground-500">{item.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14">
                <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                  联系我们
                </h2>
                <div className="bg-background-100 rounded-lg p-5 md:p-8">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-3">
                        <i className="ri-mail-line text-xl text-primary-600"></i>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-xs text-foreground-500">contact@bjtongshiji.cn</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-3">
                        <i className="ri-wechat-line text-xl text-accent-600"></i>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-900 mb-1">微信公众号</h3>
                      <p className="text-xs text-foreground-500">成全影视官方</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                        <i className="ri-customer-service-line text-xl text-secondary-600"></i>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-900 mb-1">客服热线</h3>
                      <p className="text-xs text-foreground-500">400-888-6666</p>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-background-200">
                    <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">
                      在线留言
                    </h3>
                    <form
                      className="space-y-4"
                      data-readdy-form
                      id="about-contact-form"
                      onSubmit={handleSubmit}
                    >
                      <input
                        type="text"
                        name="website_alt"
                        className="form-honeypot"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            姓名
                          </label>
                          <input
                            type="text"
                            id="contact-name"
                            name="name"
                            className="w-full px-3 py-2 text-sm bg-white border border-background-300 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            邮箱
                          </label>
                          <input
                            type="email"
                            id="contact-email"
                            name="email"
                            className="w-full px-3 py-2 text-sm bg-white border border-background-300 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          rows={4}
                          maxLength={500}
                          className="w-full px-3 py-2 text-sm bg-white border border-background-300 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言内容（500字以内）"
                        ></textarea>
                      </div>
                      <div>
                        <button
                          type="submit"
                          className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
                          提交留言
                        </button>
                        {formStatus === 'success' && (
                          <p className="mt-2 text-sm text-green-600">感谢您的留言，我们会尽快回复！</p>
                        )}
                        {formStatus === 'error' && (
                          <p className="mt-2 text-sm text-red-500">{formError}</p>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}